import React, { useState, useRef, useEffect } from 'react';
import './Navbar.css';
import Logo from "../assets/logo v4.png";
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Avatar, Tooltip } from '@mui/material';
import ClassIcon from '@mui/icons-material/Class';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import user from '../assets/user.png';
import edit from '../assets/edit.png';
import EditProfileModal from './EditProfileModal';

function stringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      fontSize: '0.875rem', // 可以調整這個值來改變字體大小，例如：'0.875rem' (較小) 或 '1.2rem' (較大)
    },
    children: `${name.split('').map((char, index) => index > 0 ? char : '').join('')}`,
  };
}

function DropdownItem({ img = '', text = '', onClick = null }) {
  return (
    <li className="dropdownItem" onClick={onClick}>
      <img src={img} alt={text} />
      <a>{text}</a>
    </li>
  );
}

/**
 * StudentNavbar 學生端專用導航列
 * menuGroups 格式：
 * [
 *   {
 *     title: '群組標題',
 *     items: [
 *       { icon: ClassIcon, text: '課程列表', active: false, onClick: () => {} },
 *       ...
 *     ]
 *   },
 *   ...
 * ]
 */
const StudentNavbar = ({ menuGroups = [] }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const avatarRef = useRef();
  const sidebarRef = useRef();
  const signOut = useSignOut();
  const navigate = useNavigate();
  
  const name = localStorage.getItem('name') || '學生';

  // 處理滑鼠離開自動收合
  useEffect(() => {
    const handleClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
      }
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    
    if (sidebarOpen || avatarOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sidebarOpen, avatarOpen]);

  const handleOpenEditProfile = () => {
    setIsEditProfileOpen(true);
    setAvatarOpen(false);
  };

  const handleCloseEditProfile = () => {
    setIsEditProfileOpen(false);
  };

  // 切換到老師端
  const switchToTeacher = () => {
    navigate('/teacher/preparation');
  };

  // 切換到學生端
  const switchToStudent = () => {
    navigate('/student/my-classes');
  };

  const logout = () => {
    signOut();
    navigate("/");
    localStorage.removeItem('userId');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('activityId');
    localStorage.removeItem('groupId');
    localStorage.removeItem('nodeId');
    localStorage.removeItem('nodeDataLength');
    localStorage.removeItem('jwtToken');
  };
  
  return (
    <>
      {/* 上方平台標題、logo與頭像 */}
      <nav className="main-navbar modern-navbar" style={{
        position:'fixed',
        top:0,
        left:0,
        right:0,
        height:64,
        display:'flex',
        alignItems:'center',
        justifyContent:'space-between',
        background:'#2b687f',
        color:'#fff',
        zIndex:2000,
        boxSizing: 'border-box'
      }}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <img alt="logo" src={Logo} className="navbar-logo circle" style={{width:40,height:40,background:'#fff'}} />
          <span className="navbar-title" style={{fontSize:'1.35rem',fontWeight:'bold',letterSpacing:2,color:'#fff'}}>想法探究平台</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          {/* 頭像下拉選單 */}
          <div ref={avatarRef} style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
            <div className="menu-trigger" onClick={() => setAvatarOpen(!avatarOpen)}>
              <Avatar {...stringAvatar(name)} />
            </div>
            <div className={`dropdown-menu ${avatarOpen ? 'active' : 'inactive'}`}>
              <h3>嗨！{name}</h3>
              <ul>
                <DropdownItem
                  img={edit}
                  text={"編輯個人資料"}
                  onClick={handleOpenEditProfile}
                />

              </ul>
              <h3><button className='register-button1' onClick={logout}>登出</button></h3>
            </div>
          </div>
        </div>
      </nav>
      {/* 左下側側邊欄，支援收合，滑鼠離開自動收合 */}
      <aside ref={sidebarRef} className={`navbar-sidebar${sidebarOpen ? '' : ' collapsed'}`} style={{
        position:'fixed',
        top:64,
        left:0,
        bottom:0,
        width:sidebarOpen?220:60,
        minWidth:sidebarOpen?220:60,
        background:'#f7fafd',
        borderRight:'1.5px solid #e0e6ed',
        display:'flex',
        flexDirection:'column',
        height:'calc(100vh - 64px)',
        zIndex:1000,
        transition:'width 0.2s'
      }}>
        <div className="sidebar-header" style={{height:32}}></div>
        <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(o => !o)} style={{
          background:'none',
          border:'none',
          cursor:'pointer',
          fontSize:22,
          margin:'32px auto 12px auto',
          display:'block'
        }}>
          {sidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </button>
        {menuGroups.map((group, idx) => (
          <div key={group.title} className="sidebar-group">
            {sidebarOpen && <div className="sidebar-title" style={{fontWeight:'bold',color:'#2b687f',margin:'8px 0 4px 16px'}}>{group.title}</div>}
            <ul style={{paddingLeft:sidebarOpen?16:0}}>
              {group.items.map((item, i) => {
                const tooltipText = item.tooltip || item.text;
                const menuItem = (
                  <li key={item.text} className={item.active ? 'active' : ''} style={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:sidebarOpen?'flex-start':'center',
                    padding:sidebarOpen?'4px 8px':'4px 0',
                    minHeight:40,
                    height:40,
                    cursor:'pointer',
                    borderRadius:6,
                    background:item.active?'#eaf2f5':'none',
                    color:item.active?'#2b687f':'#444',
                    fontWeight:item.active?'bold':'normal',
                    marginBottom:2,
                    lineHeight:'1.2',
                    gap:sidebarOpen?12:0
                }} onClick={item.onClick}>
                  <span className="sidebar-icon" style={{
                    fontSize:sidebarOpen?22:24,
                    margin:0,
                    display:'inline-flex',
                    alignItems:'center',
                    justifyContent:'center',
                    width:40,
                    height:40,
                    textAlign:'center',
                    flexShrink:0
                  }}>
                    {React.createElement(item.icon, {
                      style: { fontSize: sidebarOpen ? 22 : 24 }
                    })}
                  </span>
                  {sidebarOpen && <span className="sidebar-text" style={{marginLeft:0}}>{item.text}</span>}
                </li>
                );
                
                return sidebarOpen ? menuItem : (
                  <Tooltip key={item.text} title={tooltipText} placement="right" arrow enterDelay={300}>
                    {menuItem}
                  </Tooltip>
                );
              })}
            </ul>
          </div>
        ))}
      </aside>
      <EditProfileModal open={isEditProfileOpen} onClose={handleCloseEditProfile} />
    </>
  );
};

export default StudentNavbar;





